/**
 * Enveloppe de `fetch` pour l'API. Elle existe pour une raison unique : le 401
 * doit devenir une erreur typee, que le magasin de session reconnait et traduit
 * en « il faut se reconnecter ». Sans ce point de passage, chaque appel du front
 * devrait penser a le faire, et l'un d'eux oublierait.
 */

export class NonAuthentifie extends Error {
  constructor() {
    super("Session expiree");
  }
}

export class TropDeRequetes extends Error {
  constructor() {
    super("Trop de tentatives. Reessayez dans quelques minutes.");
  }
}

export class ErreurApi extends Error {
  constructor(
    message: string,
    readonly statut: number,
    /**
     * Le corps de la reponse. Le 409 d'un enregistrement y met les deux
     * revisions : sans elles, l'interface ne peut dire que « conflit », ce qui
     * n'indique pas quoi faire.
     */
    readonly corps?: unknown,
  ) {
    super(message);
  }
}

interface Options {
  readonly methode?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  readonly corps?: unknown;
}

export async function appelerApi<T>(chemin: string, options: Options = {}): Promise<T> {
  const methode = options.methode ?? "GET";
  const reponse = await fetch(`/api${chemin}`, {
    method: methode,
    // Meme origine : le front et l'API sont derriere le meme hote, et c'est ce
    // qui permet de se passer de CORS entierement.
    credentials: "same-origin",
    headers: options.corps === undefined ? {} : { "Content-Type": "application/json" },
    ...(options.corps === undefined ? {} : { body: JSON.stringify(options.corps) }),
  });

  if (reponse.status === 401) throw new NonAuthentifie();
  if (reponse.status === 429) throw new TropDeRequetes();

  if (!reponse.ok) {
    const corps = await lireCorps(reponse);
    throw new ErreurApi(messageDe(corps, reponse.status), reponse.status, corps);
  }

  // 204 : pas de corps a lire. `reponse.json()` jetterait.
  if (reponse.status === 204) return undefined as T;
  return (await reponse.json()) as T;
}

async function lireCorps(reponse: Response): Promise<unknown> {
  try {
    return await reponse.json();
  } catch {
    // Corps illisible ou vide : le code suffira.
    return undefined;
  }
}

function messageDe(corps: unknown, statut: number): string {
  const message = (corps as { message?: unknown } | null | undefined)?.message;
  if (typeof message === "string") return message;
  // NestJS rend un tableau quand plusieurs champs sont fautifs.
  if (Array.isArray(message)) return message.join(" · ");
  return `Erreur ${statut}`;
}
